/// <reference path="../types.d.ts" />

import { enableMapSet } from 'immer';
import { createReducer, AnyAction } from '@reduxjs/toolkit';
import InitialState from './initial-state';
import * as CaseActions from '../actions/cases.action';
import * as ContextActions from '../actions/contexts.action';
import { docMetadataAvailable } from '../actions/case-documents.actions';
import { LOAD_STATES } from './common';
import {
  cmxDateTime,
  cmxArtifacts,
  commonEnums as enums,
  highlighterUtils,
  userUtils,
  caseUtils,
  bpmSteps,
  chargeConverters,
  activationProcessor,
  vitalware,
  commonEnums,
  cmxTypeguards,
  cmxNotes,
  appServiceLines,
  uiActions,
  documentUtils,
  AmplifyCore,
  dictionaryUtils
} from '@codametrix/ui-common';
import {
  isLoadingClaim,
  loadStateClaim
} from './reducer-utils';
import { bpmTasksAvailable } from '../actions/bpm-steps.action';
import { DefaultGroupedCases } from './state/cases';
import * as ServiceLineActions from '../actions/service-lines';
import { buildRoleSet } from './users/roles';
import { fetchContexts, replaceUIState } from '../actions/ui';
import * as UIActions from '../actions/ui';
import { jumpContext } from '../actions/contexts.action';
import { formErrors } from '../stubs/form-errors';
import { roleShim } from '../actions/_action-utilities';

enableMapSet();

const { FilterKeys, CaseArtifactTypes, CustomerPhase } = enums;
const { getArtifactSet } = cmxArtifacts;

const CODER_SET = new Set([
  CaseArtifactTypes.PROCESSED,
  CaseArtifactTypes.SUBMITTED,
  CaseArtifactTypes.CODED
]);

const submitterSet = new Set([commonEnums.InvoiceTypes.SUBMITTED]);
const originalSet = new Set([commonEnums.InvoiceTypes.PROCESSED]);

// Helper method to create a new filterLoadStates Map
const createFilterLoadStatesMap = (
  existingFilterLoadStates: Map<string, CMxCommonApp.Loading> | any
): Map<string, CMxCommonApp.Loading> => {
  return existingFilterLoadStates instanceof Map
    ? new Map(existingFilterLoadStates)
    : new Map();
};

export const decorateNotes = (notes: CMxAPI.Note[]) => {
  return notes.map(note => {
    const withDecodedContent =
      cmxTypeguards.isIPCNote(note) &&
      note.encoding === 'base64' &&
      note.type !== 'text/html'
        ? { ...note, content: cmxNotes.convertBase64(note.content) }
        : note;

    const segments = highlighterUtils.segmentize(withDecodedContent);
    return documentUtils.decoratedNoteFactory({
      ...withDecodedContent,
      segments,
      spans: []
    });
  });
};

const getNotes = (caseRun: CMxAPI.CaseRun) => {
  const { notes } = caseRun;
  return decorateNotes(notes as CMxAPI.Note[]);
};

const getStartState = (state: CMx.CaseState) => {
  return state.loadState.initial ? state.loadState : LOAD_STATES.started;
};

const cleanDetail = Object.freeze({
  codeDescriptions: InitialState.cases.codeDescriptions,
  actionsState: { ...InitialState.cases.actionsState },
  artifactList: [],
  notes: [],
  codeEdits: InitialState.cases.codeEdits,
  activeItem: InitialState.cases.activeItem,
  submittedItem: InitialState.cases.submittedItem
});

const runTypes = InitialState.cases.runTypes;

export const sortCaseRuns = (
  caseDetail: CMxAPI.Case,
  caseRuns: CMxAPI.SortedCaseRuns
) => {
  runTypes.forEach(type => {
    caseRuns[type] = caseUtils.getCaseRuns(caseDetail, type);
  });

  return caseRuns;
};

const addDescriptionsToSimplifiedInvoices = (
  simplifiedInvoices: CMxCommonApp.SimplifiedInvoice[],
  codeDescriptions: CMxAPI.CodeResponse
) => {
  const withDescription = (code: any) => {
    const key = ['code', 'diagnosis_code', 'modifier_code'].find(
      key => code[key]
    );
    if (key) {
      const description: any = codeDescriptions[code[key]];
      return {
        ...code,
        description: dictionaryUtils.extractCodeDescriptionForCode(description)
      };
    }
    return code;
  };

  return simplifiedInvoices.map(invoice => {
    const transactions = invoice.transactions.map(txn => ({
      ...txn,
      codes: txn.codes.map(withDescription)
    }));

    const dx = (invoice.shared_codes?.diagnoses ?? []).map(withDescription);
    const mods = (invoice.shared_codes?.modifiers ?? []).map(withDescription);

    return {
      ...invoice,
      transactions,
      ...(invoice.shared_codes != null
        ? {
            shared_codes: {
              ...invoice.shared_codes,
              diagnoses: dx,
              modifiers: mods
            }
          }
        : {})
    };
  });
};

const reducer = createReducer(InitialState.cases, builder => {
  builder.addCase(UIActions.logout.type, () => ({ ...InitialState.cases }));
  builder.addCase(jumpContext.pending.type, () => ({ ...InitialState.cases }));

  builder.addCase('BOOTSTRAP/fulfilled', (state, action: AnyAction) => {
    const context = action.payload;
    return { ...state, context };
  });

  builder.addCase(ContextActions.displayContexts.type, (state, action: AnyAction) => {
    const contexts = action.payload;
    return { ...state, contexts, loading: false };
  });

  builder.addCase(CaseActions.featureActivation, (state, action) => {
    const featureActivation = JSON.parse(JSON.stringify(action.payload));
    const { notes } = state;
    const activations = activationProcessor(
      featureActivation,
      state.activations.panel,
      state.activations.property,
      state.activations.spans
    );
    if (notes.length === 0) {
      return { ...state, activations };
    }
    const note = notes[0] ?? {};
    const boundedSpans = highlighterUtils.normalizeSpans(
      note.segments || [],
      activations.spans.body ?? []
    );
    // in the case of no notes
    const returnedNotes: CMxCommonApp.DecoratedNote[] = [
      { ...note, spans: boundedSpans }
    ];
    return { ...state, activations, notes: returnedNotes };
  });

  builder.addCase(CaseActions.detailToggleArtifacts, (state, action) => {
    const toggleAll = action.payload;
    const { activeItem: firstRun, submittedItem, codeEdits, roles } = state;

    const artifactSet = userUtils.isCoder(roles)
      ? CODER_SET
      : getArtifactSet(toggleAll, firstRun);

    let initialInvoices =
      chargeConverters.simplifiedInvoices(firstRun, true, {
        ASSEMBLED: codeEdits
      }) ?? [];

    if (firstRun.artifacts.length) {
      // convert artifacts to invoices so that we have something to display on the UI.
      const artifacts = firstRun.artifacts.filter(
        artifact => artifact.type === CaseArtifactTypes.PREDICTION
      );
      const convertedInvoices = cmxArtifacts.artifactToSimplifiedInvoice(
        artifacts
      );
      initialInvoices = [...initialInvoices, ...convertedInvoices];
    }
    // handle rolled units.
    if (submittedItem) {
      initialInvoices = cmxArtifacts.handleImportedCases(
        initialInvoices,
        submittedItem,
        submitterSet,
        originalSet
      );
    }

    let simplifiedInvoices = initialInvoices
      .filter(invoice => artifactSet.has(invoice.type))
      .sort(cmxArtifacts.sortInvoices);

    simplifiedInvoices = addDescriptionsToSimplifiedInvoices(
      simplifiedInvoices || [],
      state.codeDescriptions
    );

    return {
      ...state,
      artifactSet,
      simplifiedInvoices,
      artifactShowAll: toggleAll
    };
  });

  builder.addCase(bpmTasksAvailable, (state, action) => {
    const evidenceList = action.payload;
    const { activeItem } = state;
    const stages = bpmSteps.stepsToStages(
      evidenceList[activeItem.process_instance_id]
    );
    return { ...state, stages };
  });

  builder.addCase(CaseActions.list.pending, (state, action) => {
    const loadState = getStartState(state);
    const listLoadState = loadStateClaim(loadState, 'list');
    return {
      ...state,
      ...cleanDetail,
      loadState: listLoadState,
      refreshList: false
    };
  });

  builder.addCase(CaseActions.list.rejected, (state, action) => {
    const isListLoading =
      isLoadingClaim(state.loadState) && state.loadState.claim === 'list';
    const loadState = isListLoading
      ? loadStateClaim(LOAD_STATES.failed, 'list')
      : { ...state.loadState };

    return { ...state, loadState };
  });

  builder.addCase(CaseActions.list.fulfilled, (state, action) => {
    const result = action.payload;
    const { content } = result;

    const { sortablePageable } = state;
    const sp: CMxCommonApp.SortablePageable<any> = {
      ...sortablePageable,
      pageableDefinition: result
    };
    const apiResponse: CMx.ApiResponse<CMxAPI.Case> = { ...state.apiResponse };
    apiResponse.items = content;

    const items = content
      .filter(item => {
        return item.id !== null && item.caseRuns.length > 0;
      })
      .map(caseUtils.getCaseRun);

    const isListLoading =
      isLoadingClaim(state.loadState) && state.loadState.claim === 'list';
    const loadState = isListLoading
      ? loadStateClaim(LOAD_STATES.done, 'list')
      : { ...state.loadState };

    return {
      ...state,
      pageable: result,
      sortablePageable: sp,
      loadState,
      items
    };
  });

  builder.addCase(CaseActions.getDictionary.pending, state => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('procedure_type_category', LOAD_STATES.started);
    return { ...state, filterLoadStates: Object.fromEntries(filterLoadStates) };
  });

  builder.addCase(CaseActions.getDictionary.rejected, state => {
    return {
      ...state
    };
  });

  builder.addCase(CaseActions.amplifyProcessInstance.pending, state => {
    return {
      ...state,
      actionsState: { ...InitialState.cases.actionsState }
    };
  });

  builder.addCase(CaseActions.amplifyProcessInstance.rejected, state => {
    const actionsState = { ...state.actionsState };
    actionsState.launchAmplify = {
      enabled: false,
      visible: true
    };

    return {
      ...state,
      actionsState
    };
  });

  const amplifyDoneHandler = (state: CMx.CaseState, action: { payload: CMxAPI.InvoicesResponse }) => {
    const result = action.payload;
    const actionsState = { ...state.actionsState };
    const { roles, activeItem } = state;
    const isSupportEngineer = roles.find(role => role === 'support_engineer');
    const isProfessionalServices = roles.find(
      role => role === 'professional_services'
    );
    const isCodingManager = roles.find(role => role === 'coding_manager');
    const isCqaManager = roles.find(role => role === 'cqa_manager');
    const isCqaCoder = roles.find(role => role === 'cqa_coder');
    const isCoder = roles.find(role => role === 'coder');
    const isCoordinator = roles.find(role => role === 'coordinator');

    if (result) {
      // if amplify knows about the case, then the case
      // has already been imported and is ready to launch
      actionsState.launchAmplify = {
        enabled: true,
        visible:
          isSupportEngineer ||
          isProfessionalServices ||
          isCodingManager ||
          isCqaManager ||
          isCqaCoder ||
          isCoder ||
          isCoordinator
      };

      actionsState.importAmplify = {
        enabled: false,
        visible: false
      };
    } else {
      const { uiStatus } = commonEnums;

      const terminalStatus = new Set<string>([
        uiStatus.AUTOMATED,
        uiStatus.CANCELLED,
        uiStatus.UPDATED_MANUAL,
        // leaving out dormant for now.
        // uiStatus.DORMANT,
        uiStatus.MANUAL,
        'active'
      ]);

      const { CaseArtifactTypes } = commonEnums;

      const requiredInvoices = new Set<string>([
        CaseArtifactTypes.ASSEMBLED,
        CaseArtifactTypes.CLINICAL,
        CaseArtifactTypes.MAPPED
      ]);

      const invoices: any = (activeItem.invoices ?? []).length
        ? activeItem.invoices
        : activeItem.artifacts;

      const hasRequired = (invoices ?? []).some((invoice: any) =>
        requiredInvoices.has(invoice.type)
      );

      const hasImportAmplify = state.context.activeContext
        ? AmplifyCore.benchEditable.isResourceAllowed(
            state.context.activeContext,
            enums.ResourceId.IMPORT_AMPLIFY,
            activeItem.service_line
          )
        : false;

      const importable =
        terminalStatus.has(activeItem.case_status) &&
        hasRequired &&
        hasImportAmplify;
      let isImportVisible = false;
      if (isSupportEngineer || isProfessionalServices) {
        isImportVisible = true;
      }
      actionsState.importAmplify = {
        enabled: importable,
        visible: isImportVisible
      };
      actionsState.launchAmplify = {
        enabled: false,
        visible: false
      };
    }

    return {
      ...state,
      activeItem: {
        ...state.activeItem,
        case_type: appServiceLines.isIPC(state.activeItem.service_line)
          ? commonEnums.UIModesEnum.STANDALONE
          : result?.caseType
      },
      actionsState
    };
  };

  builder.addCase(CaseActions.amplifyProcessInstance.fulfilled, amplifyDoneHandler as any);
  builder.addCase(CaseActions.importToAmplify.fulfilled, amplifyDoneHandler as any);

  builder.addCase(CaseActions.getDictionary.fulfilled, (state, action) => {
    const params = action.meta.arg;
    const result = action.payload;
    let index = state.criteriaFilter.filterForms.findIndex(criteriaForm => {
      return criteriaForm.key === params.key;
    });

    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('procedure_type_category', LOAD_STATES.done);

    const updatedFilterForms = state.criteriaFilter.filterForms.map(
      (form, i) => {
        if (i !== index) return form;
        return {
          ...form,
          formDefinition: {
            ...form.formDefinition,
            fieldGroups: [result]
          }
        };
      }
    );

    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filterForms: updatedFilterForms
      },
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.fetchCodeRequests.fulfilled, (state, action) => {
    const descriptions = action.payload;
    const codeDescriptions = { ...state.codeDescriptions, ...descriptions };
    return {
      ...state,
      codeDescriptions,
      loadState: LOAD_STATES.done
    };
  });

  // This is split off from the top bc need to attach descriptions
  // to the invoices.  In the case where we're using artifacts, we only
  // need to pass the map to the case detail view.
  builder.addCase(CaseActions.fetchInvoiceCodeDescriptions, (state, action) => {
    const descriptions = action.payload;
    const codeDescriptions = { ...state.codeDescriptions, ...descriptions };
    const simplifiedInvoices = addDescriptionsToSimplifiedInvoices(
      state.simplifiedInvoices || [],
      codeDescriptions
    );
    return {
      ...state,
      codeDescriptions,
      simplifiedInvoices,
      loadState: LOAD_STATES.done
    };
  });

  builder.addCase(CaseActions.fetchCodeRequests.rejected, state => {
    return {
      ...state
    };
  });

  builder.addCase(CaseActions.getCaseFilter.pending, state => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('case_status', LOAD_STATES.started);
    return { ...state, filterLoadStates: Object.fromEntries(filterLoadStates) };
  });

  builder.addCase(CaseActions.getCaseFilter.rejected, state => {
    return {
      ...state
    };
  });

  builder.addCase(CaseActions.getCaseFilter.fulfilled, (state, action) => {
    const params = action.meta.arg;
    const result = action.payload;
    let index = state.criteriaFilter.filterForms.findIndex(
      criteriaForm => criteriaForm.key === params.key
    );
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('case_status', LOAD_STATES.done);

    const updatedFilterForms = state.criteriaFilter.filterForms.map(
      (form, i) => {
        if (i !== index) return form;
        return {
          ...form,
          formDefinition: {
            ...form.formDefinition,
            fieldGroups: [result]
          }
        };
      }
    );

    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filterForms: updatedFilterForms
      },
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.clearAllFilters, (state, action) => {
    return {
      ...state,
      filterLoadStates: InitialState.cases.filterLoadStates,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: state.criteriaFilter.filters.filter(filter =>
          ['primary_clinical_identifier', 'patient_mrn', 'id'].includes(
            filter.key
          )
        ),
        dateFilter: undefined
      },
      sortablePageable: {
        ...state.sortablePageable,
        filterableOptions: {
          ...state.sortablePageable.filterableOptions,
          filters: state.criteriaFilter.filters.filter(filter =>
            ['primary_clinical_identifier', 'patient_mrn', 'id'].includes(
              filter.key
            )
          ),
          dateFilter: undefined
        }
      }
    };
  });

  builder.addCase(CaseActions.applyFilters.pending, state => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('criteriaFilter', LOAD_STATES.started);
    return { ...state, filterLoadStates: Object.fromEntries(filterLoadStates) };
  });

  builder.addCase(CaseActions.applyFilters.fulfilled, (state, action) => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('criteriaFilter', LOAD_STATES.done);

    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filters: action.payload
      },
      sortablePageable: {
        ...state.sortablePageable,
        filterableOptions: {
          ...state.sortablePageable.filterableOptions,
          filters: action.payload
        }
      },
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.applyDateFilter.pending, state => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('applyDateFilter', LOAD_STATES.started);
    return {
      ...state,
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.applyDateFilter.fulfilled, (state, action) => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set('applyDateFilter', LOAD_STATES.done);
    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        dateFilter: action.payload
      },
      sortablePageable: {
        ...state.sortablePageable,
        filterableOptions: {
          ...state.sortablePageable.filterableOptions,
          filters: [...(state.criteriaFilter.filters ?? [])],
          dateFilter: action.payload
        }
      },
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.getBillingGroup.pending, state => {
    return {
      ...state,
      billingGroupLoadState: LOAD_STATES.initial,
      groupedCases: InitialState.cases.groupedCases
    };
  });

  builder.addCase(CaseActions.getBillingGroup.fulfilled, (state, action) => {
    const result = action.payload;
    const { activeItem } = state;
    const groupedCases = caseUtils.getGroupedCases(
      activeItem,
      result.content,
      'billing'
    );
    return {
      ...state,
      billingGroupLoadState: LOAD_STATES.done,
      groupedCases
    };
  });

  builder.addCase(CaseActions.getDetail.pending, state => {
    const detailLoadState = loadStateClaim(LOAD_STATES.started, 'detail');

    return {
      ...state,
      ...cleanDetail,
      activations: { ...InitialState.cases.activations },
      stages: [],
      groupedCases: { ...DefaultGroupedCases },
      loadState: detailLoadState,
      artifactLoadState: LOAD_STATES.initial,
      formErrors: formErrors.noErrors,
      caseRuns: {},
      htmlNotes: []
    };
  });

  builder.addCase(CaseActions.getDetail.rejected, (state, action) => {
    const error = action.error;
    const isDetailLoading =
      isLoadingClaim(state.loadState) && state.loadState.claim === 'detail';
    const loadState = isDetailLoading
      ? loadStateClaim(LOAD_STATES.failed, 'detail')
      : { ...state.loadState };

    return {
      ...state,
      ...cleanDetail,
      codeEdits: InitialState.cases.codeEdits,
      codeDescriptions: InitialState.cases.codeDescriptions,
      loadState,
      formErrors: (error as any).errors
    };
  });

  builder.addCase(CaseActions.getSubmitterProcess.fulfilled, (state, action) => {
    const submitterCase = action.payload;
    if (!submitterCase) {
      return { ...state };
    }

    const { simplifiedInvoices: originalInvoices } = state;
    const submittedItem = caseUtils.getCaseRun(submitterCase);
    const simplifiedInvoices = cmxArtifacts.handleImportedCases(
      originalInvoices,
      submittedItem,
      submitterSet,
      originalSet
    );

    return {
      ...state,
      submittedItem,
      simplifiedInvoices,
      artifactLoadState: LOAD_STATES.done
    };
  });

  builder.addCase(CaseActions.getDetail.fulfilled, (state, action) => {
    const caseDetail = action.payload;
    const { submittedItem } = state;
    const stateMixin: { notes?: any[] } = {};

    const apiResponse: CMx.ApiResponse<CMxAPI.Case> = { ...state.apiResponse };
    apiResponse.activeItem = caseDetail;

    const caseRun = caseUtils.getCaseRun(caseDetail);
    const artifactLoadState =
      caseRun.submitter_process === false
        ? LOAD_STATES.initial
        : LOAD_STATES.done;

    const activeItem = cmxArtifacts.preprocessor(caseRun);
    const codeEdits = vitalware.parseCodeEdits(activeItem);
    // build evidence manager so we can get notes
    const { artifactShowAll, caseRuns, roles } = state;
    const artifactSet = userUtils.isCoder(roles)
      ? CODER_SET
      : getArtifactSet(artifactShowAll, activeItem);

    if (!appServiceLines.isIPC(activeItem.service_line)) {
      let newNotes = [...getNotes(activeItem)];
      newNotes = newNotes.filter(note => note.document_id !== null);

      newNotes.sort(documentUtils.compareDecoratedNotesForNav);
      stateMixin.notes = newNotes;
    }

    const isDetailLoading =
      isLoadingClaim(state.loadState) && state.loadState.claim === 'detail';
    const loadState = isDetailLoading
      ? loadStateClaim(LOAD_STATES.done, 'detail')
      : { ...state.loadState };

    const sortedCaseRuns = sortCaseRuns(caseDetail, { ...caseRuns });

    let initialInvoices =
      chargeConverters.simplifiedInvoices(caseRun, true, {
        ASSEMBLED: codeEdits
      }) ?? [];

    // not all caseRuns have artifacts.
    if (caseRun.artifacts?.length) {
      // For Radv2
      // convert artifacts to invoices so that we have something to display on the UI.
      const artifacts = caseRun.artifacts.filter(
        artifact => artifact.type === CaseArtifactTypes.PREDICTION
      );
      const convertedInvoices = cmxArtifacts.artifactToSimplifiedInvoice(
        artifacts
      );
      initialInvoices = [...initialInvoices, ...convertedInvoices];
    }
    // handle rolled units.
    if (submittedItem) {
      initialInvoices = cmxArtifacts.handleImportedCases(
        initialInvoices,
        submittedItem,
        submitterSet,
        originalSet
      );
    }

    let simplifiedInvoices = initialInvoices
      .filter(invoice => artifactSet.has(invoice.type))
      .sort(cmxArtifacts.sortInvoices);

    simplifiedInvoices = addDescriptionsToSimplifiedInvoices(
      simplifiedInvoices || [],
      state.codeDescriptions
    );

    return {
      ...state,
      ...stateMixin,
      artifactLoadState,
      apiResponse,
      activeItem,
      codeEdits,
      artifactSet,
      loadState,
      simplifiedInvoices,
      formErrors: formErrors.noErrors,
      caseRuns: sortedCaseRuns
    };
  });

  builder.addCase(CaseActions.getTenantFilter.pending, state => {
    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set(FilterKeys.TENANT, LOAD_STATES.started);
    return { ...state, filterLoadStates: Object.fromEntries(filterLoadStates) };
  });

  builder.addCase(CaseActions.getTenantFilter.fulfilled, (state, action) => {
    const result = action.payload;
    let index = state.criteriaFilter.filterForms.findIndex(
      criteriaForm => criteriaForm.key === FilterKeys.TENANT
    );

    const filterLoadStates = createFilterLoadStatesMap(state.filterLoadStates);
    filterLoadStates.set(FilterKeys.TENANT, LOAD_STATES.done);

    const updatedFilterForms = state.criteriaFilter.filterForms.map(
      (form, i) => {
        if (i !== index) return form;
        if (
          form.formDefinition.fieldGroups === undefined ||
          form.formDefinition.fieldGroups.length === 0
        ) {
          return form;
        }
        return {
          ...form,
          formDefinition: {
            ...form.formDefinition,
            fieldGroups: [
              {
                ...form.formDefinition.fieldGroups[0],
                fields: result
              },
              ...form.formDefinition.fieldGroups.slice(1)
            ]
          }
        };
      }
    );

    return {
      ...state,
      criteriaFilter: {
        ...state.criteriaFilter,
        filterForms: updatedFilterForms
      },
      filterLoadStates: Object.fromEntries(filterLoadStates)
    };
  });

  builder.addCase(CaseActions.applyDefaultFilters, (state, action) => {
    // Build plain `sortablePageable` / `criteriaFilter` copies, then mutate those locals.
    // This does not mutate the Immer draft; the next return replaces `state.sortablePageable`.
    const context = action.payload;
    let criteriaFilter = { ...state.criteriaFilter, filters: [...state.criteriaFilter.filters] };
    let sortablePageable = {
      ...state.sortablePageable,
      filterableOptions: {
        ...state.sortablePageable.filterableOptions,
        filters: state.sortablePageable.filterableOptions?.filters
          ? [...state.sortablePageable.filterableOptions.filters]
          : state.sortablePageable.filterableOptions?.filters
      }
    };

    criteriaFilter.dateFilter = {
      key: `date_of_service`,
      type: `BETWEEN`,
      from: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE, {
        throw: false,
        localized: true,
        offset: {
          action: cmxDateTime.DateManipulateOperation.SUBTRACT,
          delta: [2, cmxDateTime.DateManipulateUnit.DAY]
        }
      }),
      to: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE)
    };

    sortablePageable.filterableOptions.dateFilter = {
      key: `date_of_service`,
      from: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE, {
        throw: false,
        localized: true,
        offset: {
          action: cmxDateTime.DateManipulateOperation.SUBTRACT,
          delta: [2, cmxDateTime.DateManipulateUnit.DAY]
        }
      }),
      to: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE)
    };

    let freshFilterForms = [];

    const isProfServiceContext = context.activeContext
      ? AmplifyCore.benchEditable.isResourceAllowed(
          context.activeContext,
          'power_features',
          context.activeContext.serviceLineName ?? ''
        )
      : false;

    if (isProfServiceContext) {
      freshFilterForms = criteriaFilter.filterForms.map(filterForm => {
        if (filterForm.key === 'customer_phase') {
          return {
            ...filterForm,
            disabled: false
          };
        }
        return filterForm;
      });
    } else {
      freshFilterForms = criteriaFilter.filterForms.map(filterForm => {
        if (filterForm.key === 'customer_phase') {
          return {
            ...filterForm,
            disabled: true
          };
        }
        return filterForm;
      });

      const filterGoLive = {
        key: 'customer_phase',
        terms: [CustomerPhase.LIVE],
        type: 'IN'
      };

      if (sortablePageable.filterableOptions.filters !== undefined) {
        sortablePageable.filterableOptions.filters.push(filterGoLive);
      } else {
        sortablePageable.filterableOptions.filters = [filterGoLive];
      }

      criteriaFilter.filters.push(filterGoLive);
    }

    return {
      ...state,
      criteriaFilter: { ...criteriaFilter, filterForms: freshFilterForms },
      sortablePageable
    };
  });

  builder.addCase(uiActions.chooseContext.type, (state, action: AnyAction) => {
    const payload = action.payload;
    return { ...state, context: { ...payload }, roles: buildRoleSet(payload) };
  });

  builder.addCase(CaseActions.showProcessingTimeline, (state, action) => {
    const data = action.payload;
    return {
      ...state,
      displayTimeline: data
    };
  });

  builder.addCase(CaseActions.setActiveProcess.pending, (state, action) => {
    const payload = action.meta.arg;
    const { caseRun } = payload;
    const { codeEdits, roles, artifactShowAll } = state;

    const artifactSet = userUtils.isCoder(roles)
      ? CODER_SET
      : getArtifactSet(artifactShowAll, caseRun);

    const firstPass = chargeConverters.simplifiedInvoices(caseRun, true, {
      ASSEMBLED: codeEdits
    });

    const simplifiedInvoices = firstPass
      .filter(invoice => artifactSet.has(invoice.type))
      .sort(cmxArtifacts.sortInvoices);

    return {
      ...state,
      simplifiedInvoices,
      activeItem: caseRun
    };
  });

  builder.addCase(CaseActions.caseOrganizationContext, (state, action) => {
    const caseOrganizationContext = action.payload;
    return {
      ...state,
      caseOrganizationContext
    };
  });

  builder.addCase(CaseActions.navigateDetail.pending, (state, action) => {
    const caseRun = action.meta.arg;
    return {
      ...state,
      activeItem: caseRun
    };
  });

  builder.addCase(ServiceLineActions.navigateServiceLine.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.initial
    };
  });

  builder.addCase(CaseActions.getDocumentsAsHtml.fulfilled, (state, action) => {
    const htmlNotes = action.payload;
    const newNotes = [...htmlNotes]
      .filter(note => note.document_id !== null)
      .map(documentUtils.shimSegmentsIfNeeded)
      .sort(documentUtils.compareDecoratedNotesForNav);

    return {
      ...state,
      notes: newNotes
    };
  });

  builder.addCase(docMetadataAvailable, (state, action) => {
    const docs = action.payload;
    return {
      ...state,
      // Preserve caller order (e.g. GET_HTML aligns metadata to sorted notes); do not mutate payload.
      documentMetadata: [...docs]
    };
  });

  builder.addCase(replaceUIState.type, (state, action: AnyAction) => {
    const uiState = action.payload;
    const roles = roleShim(uiState?.context, state.roles);
    return {
      ...state,
      roles
    };
  });

  builder.addCase(CaseActions.getShowOrderNumber.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.started
    };
  });

  builder.addCase(CaseActions.getShowOrderNumber.fulfilled, (state, action) => {
    const result = action.payload;
    let showOrderNumber = false;
    if (result?.value) {
      try {
        showOrderNumber = JSON.parse(result.value);
      } catch {
        // non-JSON value — treat as falsy
      }
    }
    return {
      ...state,
      showOrderNumber,
      loadState: LOAD_STATES.done
    };
  });

  builder.addCase(CaseActions.getWorkItemDetails.fulfilled, (state, action) => {
    const result = action.payload;
    return {
      ...state,
      workItem: { ...result }
    };
  });

  builder.addCase(CaseActions.updateWorkItemDismissed.fulfilled, (state, action) => {
    const result = action.payload;
    // Extract the dismissed status from the response data
    const dismissed = result?.data?.dismissed;
    return {
      ...state,
      workItem: {
        ...state.workItem,
        dismissed
      }
    };
  });
});

export default reducer;
